import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle } from 'lucide-react';
import { useRole } from '../../contexts/RoleContext';
import { getDashboardRouteForRole } from '../../utils/getDashboardRouteForRole';

const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const { activeRole } = useRole();
  const dashboardRoute = getDashboardRouteForRole(activeRole);

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(dashboardRoute)}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        <div className="bg-white rounded-lg shadow-sm border border-stone-200 p-8">
          <h1 className="text-3xl font-bold text-stone-900 mb-2">Support</h1>
          <p className="text-stone-600 mb-8">
            We're here to help with any questions about your estate planning journey.
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4 p-6 bg-cyan-50 border border-cyan-100 rounded-lg">
              <MessageCircle className="h-6 w-6 text-cyan-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-semibold text-stone-900 mb-2">Live Chat</h2>
                <p className="text-stone-700 mb-3">
                  Get instant help from our support team through the chat widget in the bottom right corner.
                </p>
                <p className="text-sm text-stone-600">
                  Available Monday-Friday, 9am-5pm EST
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-stone-50 border border-stone-200 rounded-lg">
              <Mail className="h-6 w-6 text-stone-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-semibold text-stone-900 mb-2">Email Support</h2>
                <p className="text-stone-700 mb-3">
                  Send us an email and we'll get back to you within 24 hours.
                </p>
                <a
                  href="mailto:support@endofanera.ai"
                  className="text-cyan-600 hover:text-cyan-700 font-medium"
                >
                  support@endofanera.ai
                </a>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-stone-200">
              <h3 className="text-lg font-semibold text-stone-900 mb-4">Common Questions</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-stone-900 mb-1">How do I invite my executor?</h4>
                  <p className="text-sm text-stone-700">
                    Navigate to "Manage Access" in your dashboard and use the invite form to send an invitation to your executor's email.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-stone-900 mb-1">Is my data secure?</h4>
                  <p className="text-sm text-stone-700">
                    Yes. All sensitive estate planning data is encrypted using AES-256-GCM encryption on your device before being stored.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-stone-900 mb-1">Can I change my subscription?</h4>
                  <p className="text-sm text-stone-700">
                    You can manage your subscription in Settings. Contact support if you need assistance with plan changes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
